/**
 * The decoded-buffer sound engine both apps play their UI sounds through —
 * the shape of Web Audio's own answer to "a short click, many times":
 *
 *   - ONE audio context, created lazily and kept (it is the only thing that
 *     holds an output device);
 *   - each clip DECODED ONCE into a buffer, cached by key;
 *   - one throwaway buffer-source node per play — the cheapest object the
 *     graph has, built to be started once and dropped, so a retrigger never
 *     seeks or rewinds anything;
 *   - at most `maxVoices` sounding at once; a play past the cap is refused,
 *     never queued (a stacked burst of the same tick is noise, not feedback);
 *   - `ended` from the node's own end event.
 *
 * Only the context and the decoder are injected. Web hands in its page-wide
 * `AudioContext` and a fetch+decode; the app hands in react-native-audio-api's
 * `AudioContext` and its file decoder. Both satisfy the structural slices
 * below, so this module carries no platform import (it is reachable from the
 * shared sound barrel, which server bundles may touch).
 *
 * Fire-and-forget like the rest of the sound core: nothing here throws, and a
 * failure is silence.
 */

import type { SoundHandle } from './core';

/** The slice of an `AudioBuffer` the engine reads. */
export interface EngineBuffer {
  /** Seconds. */
  readonly duration: number;
}

/** The slice of an `AudioBufferSourceNode` the engine drives. */
export interface EngineSourceNode<B extends EngineBuffer> {
  buffer: B | null;
  connect(destination: never): unknown;
  disconnect(): void;
  start(when?: number, offset?: number): void;
  stop(when?: number): void;
}

/** The slice of a `GainNode` the engine drives. */
export interface EngineGainNode {
  gain: { value: number };
  connect(destination: never): unknown;
  disconnect(): void;
}

/**
 * The slice of an `AudioContext` the engine drives. `state` is a string
 * because the platforms disagree past the standard three (Safari adds
 * `interrupted`); anything but `running` means "not sounding now".
 */
export interface EngineContext<B extends EngineBuffer> {
  readonly state: string;
  readonly sampleRate: number;
  readonly destination: unknown;
  createBufferSource(): EngineSourceNode<B>;
  createGain(): EngineGainNode;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  close(): Promise<void>;
}

export interface BufferEngineOptions<B extends EngineBuffer, C extends EngineContext<B>> {
  /**
   * The context to play on, or `null` when there is none (SSR, no audio
   * hardware). Called whenever the engine has no context — once, unless the
   * engine closed the one it had ({@link BufferEngine.setHeld}).
   */
  getContext(): C | null;
  /** Decode `key`'s clip for `context`; `null` when it cannot be played. */
  decode(key: string, context: C): Promise<B | null>;
  /**
   * Call `onEnded` once when `node` finishes (or is stopped), and return an
   * unbind. Injected because the platforms spell the event differently
   * (`onended` on the web, `onEnded` in react-native-audio-api).
   */
  bindEnded(node: EngineSourceNode<B>, onEnded: () => void): () => void;
  /** Voices allowed at once; the web's uisfx player uses the same 4. */
  maxVoices?: number;
  /**
   * Whether a play may resume a context that is not running. The app: yes —
   * there is no gesture rule, and the engine suspends it when idle. The web:
   * no — only a user gesture may, so the play is dropped and a resume is
   * attempted (caught) for the next one.
   */
  resumeOnPlay: boolean;
  /**
   * Suspend the context this long after the last voice ends, so an idle app
   * holds no running output stream. Omitted = never (the web, whose context
   * is shared with the uisfx synthesizer).
   */
  idleSuspendMs?: number;
  /**
   * A play still waiting for its buffer or for the context to come back after
   * this long is dropped: a click that lands a second late is wrong.
   */
  maxStartDelayMs?: number;
  /**
   * Runs before every resume this engine asks for, and the resume waits for it
   * (the app re-asserts its audio session, which must land BEFORE the resume).
   */
  beforeResume?(): void | Promise<void>;
}

export interface BufferEngine {
  /**
   * Play `key` at `gain` (0..1, already including the master volume). `null`
   * when refused: held, over the voice cap, or no context.
   */
  play(key: string, gain: number): SoundHandle | null;
  /** Decode `key` ahead of its first play. Silent; safe to call repeatedly. */
  warm(key: string): void;
  /** Cut every voice. */
  stopAll(): void;
  /** Forget the decoded buffers whose key matches (a pack the user left). */
  forget(match: (key: string) => boolean): void;
  /**
   * Held = another feature owns the audio hardware (a live exam, a recording):
   * every voice is cut, plays are refused, and the context is CLOSED — not
   * just suspended — so nothing of this engine competes for the session. The
   * decoded buffers survive; the next play after release opens a new context.
   */
  setHeld(held: boolean): void;
  /** Voices counted against the cap right now (tests, diagnostics). */
  activeVoices(): number;
}

export const DEFAULT_MAX_VOICES = 4;
export const DEFAULT_MAX_START_DELAY_MS = 1_000;
/**
 * How long past its own length a voice may go without an end event before it
 * stops counting against the cap. The event never comes from a context that
 * was interrupted mid-clip (a call, Siri, a route change), and four such
 * voices would otherwise silence the engine for good.
 */
export const VOICE_GRACE_MS = 1_000;

type Voice = { endsBy: number; stop: () => void };

export function createBufferEngine<B extends EngineBuffer, C extends EngineContext<B>>(
  options: BufferEngineOptions<B, C>,
): BufferEngine {
  const maxVoices = options.maxVoices ?? DEFAULT_MAX_VOICES;
  const maxStartDelayMs = options.maxStartDelayMs ?? DEFAULT_MAX_START_DELAY_MS;

  let context: C | null = null;
  let decodedAtRate: number | null = null;
  let held = false;
  const buffers = new Map<string, Promise<B | null>>();
  const voices = new Set<Voice>();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * resume/suspend/close, one at a time and in the order asked. Native runs
   * each on its own thread, so an idle suspend followed at once by a play's
   * resume could otherwise land resumed-then-suspended: a started voice on a
   * silent context.
   */
  let transitions: Promise<unknown> = Promise.resolve();
  let suspending = false;
  function transition(step: () => Promise<unknown>): Promise<unknown> {
    const next = transitions.then(step, step);
    transitions = next.catch(() => {});
    return next;
  }

  function currentContext(): C | null {
    if (held) return null;
    if (context && context.state !== 'closed') return context;
    try {
      const fresh = options.getContext();
      // Buffers are decoded at their context's rate; a new context at another
      // rate (a different output route) decodes them again.
      if (fresh && fresh.sampleRate !== decodedAtRate) buffers.clear();
      if (fresh) decodedAtRate = fresh.sampleRate;
      context = fresh;
    } catch {
      context = null;
    }
    return context;
  }

  function bufferFor(key: string, ctx: C): Promise<B | null> {
    const cached = buffers.get(key);
    if (cached) return cached;
    const decoded = Promise.resolve()
      .then(() => options.decode(key, ctx))
      .catch(() => null)
      .then((buffer) => {
        // A failed decode is retried by the next play, not cached as silence.
        if (!buffer && buffers.get(key) === decoded) buffers.delete(key);
        return buffer;
      });
    buffers.set(key, decoded);
    return decoded;
  }

  /** Resolves whether `ctx` is running — resuming it first where allowed. */
  function whenRunning(ctx: C): Promise<boolean> {
    if (ctx.state === 'running' && !suspending) return Promise.resolve(true);
    if (ctx.state === 'closed') return Promise.resolve(false);
    if (!options.resumeOnPlay) {
      void ctx.resume().catch(() => {});
      return Promise.resolve(false);
    }
    return transition(async () => {
      if (context !== ctx || held) return;
      await options.beforeResume?.();
      if (context !== ctx || held) return;
      await ctx.resume();
    })
      .then(
        () => ctx.state === 'running',
        () => false,
      )
      .then((running) => {
        // A context that will not come back (its output died under it — a
        // media-services reset, a route the device dropped) is replaced, not
        // retried forever: the next play opens a fresh one.
        if (!running) discard(ctx);
        return running;
      });
  }

  function discard(ctx: C): void {
    if (context !== ctx) return;
    context = null;
    void transition(() => ctx.close()).catch(() => {});
  }

  function cancelIdle(): void {
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = null;
  }

  function scheduleIdle(): void {
    const idleMs = options.idleSuspendMs;
    if (idleMs === undefined || voices.size > 0) return;
    cancelIdle();
    idleTimer = setTimeout(() => {
      idleTimer = null;
      const ctx = context;
      if (!ctx || voices.size > 0 || ctx.state !== 'running') return;
      suspending = true;
      void transition(() => ctx.suspend()).finally(() => {
        suspending = false;
      });
    }, idleMs);
  }

  function prune(now: number): void {
    for (const voice of voices) if (now > voice.endsBy) voices.delete(voice);
  }

  return {
    play(key, gain) {
      try {
        const ctx = currentContext();
        if (!ctx) return null;
        const askedAt = Date.now();
        prune(askedAt);
        if (voices.size >= maxVoices) return null;
        cancelIdle();

        let markEnded = () => {};
        const ended = new Promise<void>((resolve) => {
          markEnded = resolve;
        });
        let node: EngineSourceNode<B> | null = null;
        let gainNode: EngineGainNode | null = null;
        let unbind = () => {};
        let finished = false;
        // Counted from the moment it is asked for, so a burst past the cap is
        // refused even while the first plays are still decoding.
        const voice: Voice = {
          endsBy: askedAt + maxStartDelayMs + VOICE_GRACE_MS,
          stop: () => finish(true),
        };
        voices.add(voice);

        function finish(cut: boolean): void {
          if (finished) return;
          finished = true;
          voices.delete(voice);
          unbind();
          try {
            if (cut) node?.stop();
          } catch {
            // Never started, or already stopped — fine.
          }
          try {
            node?.disconnect();
            gainNode?.disconnect();
          } catch {
            // Already disconnected — fine.
          }
          node = null;
          gainNode = null;
          markEnded();
          scheduleIdle();
        }

        void Promise.all([bufferFor(key, ctx), whenRunning(ctx)])
          .then(([buffer, running]) => {
            if (finished) return;
            if (
              !buffer ||
              !running ||
              context !== ctx ||
              Date.now() - askedAt > maxStartDelayMs
            ) {
              finish(false);
              return;
            }
            const source = ctx.createBufferSource();
            const level = ctx.createGain();
            source.buffer = buffer;
            level.gain.value = gain;
            source.connect(level as never);
            level.connect(ctx.destination as never);
            node = source;
            gainNode = level;
            unbind = options.bindEnded(source, () => finish(false));
            voice.endsBy = Date.now() + buffer.duration * 1000 + VOICE_GRACE_MS;
            // Explicit (0, 0): react-native-audio-api rejects some bare starts.
            source.start(0, 0);
          })
          .catch(() => finish(false));

        return { ended, stop: () => finish(true) };
      } catch {
        return null;
      }
    },

    warm(key) {
      try {
        const ctx = currentContext();
        if (ctx) void bufferFor(key, ctx);
      } catch {
        // Sound must never break a feature.
      }
    },

    stopAll() {
      for (const voice of [...voices]) voice.stop();
    },

    forget(match) {
      for (const key of [...buffers.keys()]) if (match(key)) buffers.delete(key);
    },

    setHeld(next) {
      if (next === held) return;
      held = next;
      if (!next) return;
      cancelIdle();
      this.stopAll();
      if (context) discard(context);
    },

    activeVoices() {
      prune(Date.now());
      return voices.size;
    },
  };
}
